import React, { useState } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Popover,
  Badge,
  Typography,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone'
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone'
import FilterListIcon from '@mui/icons-material/FilterList'
import FavoriteIcon from '@mui/icons-material/Favorite'
import SettingsIcon from '@mui/icons-material/Settings'
import { useNavigate } from 'react-router-dom'
import { SORT_OPTIONS, ORDER_OPTIONS, ROUTES } from '../constants'

/**
 * Composant de barre de filtres pour les livres
 */
const BooksFilterBar = ({
  title = 'Mes livres',
  search,
  onSearchChange,
  onSearchClear,
  sortBy,
  onSortChange,
  order,
  onOrderChange,
  filterValues,
  onFilterChange,
  filterIconType,
  viewMode,
  onViewModeChange,
  showManageSeries = false,
}) => {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFilterClose = () => {
    setAnchorEl(null)
  }

  const handleFilterChange = (event, newValues) => {
    if (onFilterChange) {
      onFilterChange(newValues)
    }
  }

  const activeFiltersCount = filterValues ? filterValues.length : 0

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: { xs: 1, sm: 2 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
        <h1 style={{ margin: 0 }}>{title}</h1>
        {viewMode && onViewModeChange && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newVal) => newVal && onViewModeChange(newVal)}
              size="small"
            >
              <ToggleButton value="default">Défaut</ToggleButton>
              <ToggleButton value="series">Séries</ToggleButton>
            </ToggleButtonGroup>
            {showManageSeries && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => navigate(ROUTES.SERIES)}
                sx={{ ml: 1, borderRadius: 2 }}
              >
                Gérer
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        {/* Champ de recherche */}
        <TextField
          variant="filled"
          size="small"
          label="Rechercher"
          placeholder="Harry Potter"
          aria-label="Rechercher"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            minWidth: 200,
            width: { xs: '100%', sm: 220 },
            ml: { xs: 0, sm: 1 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchTwoToneIcon />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Effacer la recherche"
                  onClick={onSearchClear}
                >
                  <ClearTwoToneIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Tri */}
        <FormControl
          variant="filled"
          size="small"
          sx={{
            minWidth: 140,
            width: { xs: '100%', sm: 140 },
            ml: { xs: 0, sm: 1 },
          }}
        >
          <InputLabel id="sort-by-label">Trier par</InputLabel>
          <Select
            labelId="sort-by-label"
            value={sortBy}
            label="Trier par"
            onChange={(e) => onSortChange(e.target.value)}
          >
            <MenuItem value={SORT_OPTIONS.TITLE}>Titre</MenuItem>
            {filterIconType === 'heart' ? (
              <MenuItem value={SORT_OPTIONS.RATING}>Note</MenuItem>
            ) : (
              <MenuItem value={SORT_OPTIONS.PRIORITY}>Envie</MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Ordre */}
        <FormControl
          variant="filled"
          size="small"
          sx={{ minWidth: 140, width: { xs: '100%', sm: 140 } }}
        >
          <InputLabel id="order-label">Ordre</InputLabel>
          <Select
            labelId="order-label"
            value={order}
            label="Ordre"
            onChange={(e) => onOrderChange(e.target.value)}
          >
            <MenuItem value={ORDER_OPTIONS.ASC}>Croissant</MenuItem>
            <MenuItem value={ORDER_OPTIONS.DESC}>Décroissant</MenuItem>
          </Select>
        </FormControl>

        {/* Filtre exact */}
        {filterValues !== undefined && onFilterChange && (
          <>
            <Badge badgeContent={activeFiltersCount} color="secondary" sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 1 } }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleFilterClick}
                startIcon={<FilterListIcon />}
                sx={{
                  height: 48,
                  width: '100%',
                  px: 2,
                  borderRadius: '12px',
                }}
              >
                Filtres
              </Button>
            </Badge>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                elevation: 4,
                sx: { borderRadius: 3, mt: 1 },
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 2.5 }, width: { xs: 'calc(100vw - 32px)', sm: 360 }, maxWidth: { xs: '100%', sm: 400 } }}>
                {/* Header du Popover */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                    Filtres
                  </Typography>
                  {activeFiltersCount > 0 && (
                    <Button
                      size="small"
                      onClick={() => handleFilterChange(null, [])}
                      sx={{ textTransform: 'none', fontSize: '0.85rem', fontWeight: 600, borderRadius: 2 }}
                    >
                      Effacer tout
                    </Button>
                  )}
                </Box>
                
                <Divider sx={{ mb: 2, opacity: 0.6 }} />

                {/* Section Notes / Envie */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {filterIconType === 'heart' ? 'Par note' : 'Par envie'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', gap: { xs: 0.5, sm: 1 } }}>
                    {[1, 2, 3, 4, 5].map((val) => {
                      const isSelected = filterValues?.includes(val)
                      return (
                        <Chip
                          key={val}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              {val} {filterIconType === 'heart' ? <FavoriteIcon sx={{ color: isSelected ? 'inherit' : '#F6658C', fontSize: '1rem' }} /> : <span style={{ fontSize: '1rem' }}>🔥</span>}
                            </Box>
                          }
                          onClick={() => {
                            const newValues = isSelected
                              ? filterValues.filter((v) => v !== val)
                              : [...(filterValues || []), val]
                            handleFilterChange(null, newValues.sort())
                          }}
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            flex: 1,
                            borderRadius: '10px',
                            fontWeight: isSelected ? 600 : 500,
                            borderWidth: '1.5px',
                            fontSize: '0.9rem',
                            py: 2,
                            px: 0.5,
                            '& .MuiChip-label': {
                              px: { xs: 0.5, sm: 1 },
                            },
                            '&:hover': {
                              borderWidth: '1.5px',
                              backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      )
                    })}
                  </Box>
                </Box>
              </Box>
            </Popover>
          </>
        )}

      </Box>
    </Box>
  )
}

export default BooksFilterBar
